
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ContactPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-black select-none cursor-pointer" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </span>
            </Link>
            {/* Existing navigation content */}
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-blue-400 transition-colors cursor-pointer">Services</Link>
              <Link to="/solutions" className="hover:text-blue-400 transition-colors cursor-pointer">Solutions</Link>
              <Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About</Link>
              <Link to="/contact" className="text-blue-400 cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button 
                className="text-gray-600 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 mt-4 py-4">
            <div className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Services
              </Link>
              <Link 
                to="/solutions" 
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Solutions
              </Link>
              <Link 
                to="/about" 
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="text-blue-400 px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/1f05d6fa3eb9a13a3172f2be42d68ff0.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="block">Get In</span>
            <span className="block text-blue-400">Touch</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-300">
            Ready to transform your warehouse operations? Contact our experts for a free consultation
          </p>
        </div>
      </section>

      {/* Contact Information & Support */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Contact Information
              </h2>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-600">info@novatronis.com</p>
                  <p className="text-sm text-gray-5">We'll respond within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Support Services */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Support Services</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Technical Support</h4>
                    <p className="text-gray-600">Global remote monitoring and troubleshooting</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Maintenance</h4>
                    <p className="text-gray-600">Preventive maintenance and system optimization</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Training</h4>
                    <p className="text-gray-600">Comprehensive staff training and documentation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Novatronis */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Novatronis</h2>
            <p className="text-xl text-gray-600">Leading the industry with proven expertise and innovative solutions</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Industry Expertise</h3>
              <p className="text-gray-600">Over 15 years of experience in warehouse automation with hundreds of successful implementations worldwide.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Cutting-Edge Technology</h3>
              <p className="text-gray-600">Latest AI-powered robotics and automation systems designed for maximum efficiency and reliability.</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Dedicated Support</h3>
              <p className="text-gray-600">Comprehensive support from consultation to implementation and ongoing maintenance with 24/7 availability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600">Delivering specialized automation solutions across diverse sectors</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shopping-cart-line',
                title: 'E-commerce',
                description: 'High-volume order fulfillment and rapid shipping solutions'
              },
              {
                icon: 'ri-truck-line',
                title: 'Logistics',
                description: 'Distribution centers and third-party logistics optimization'
              },
              {
                icon: 'ri-building-line',
                title: 'Manufacturing',
                description: 'Production line integration and inventory management'
              },
              {
                icon: 'ri-medicine-bottle-line',
                title: 'Pharmaceuticals',
                description: 'Compliance-focused automation with precision handling'
              }
            ].map((industry, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${industry.icon} text-xl text-blue-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{industry.title}</h3>
                <p className="text-gray-600 text-sm">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4 select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Transforming warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions. We empower businesses to achieve unprecedented levels of efficiency, accuracy, and cost savings through innovative technology.
              </p>
            </div>
            {/* Additional columns could be added here if needed */}
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Novatronis Solutions. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-blue-400 transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
